/**
 * SetTripRateModal — iter191.
 *
 * One-time setup for a Trip group's multi-currency mode. Admin enters the
 * forex card / Wise lock-in rate ONCE before the first expense; from then on
 * every expense and top-up is converted with that frozen rate. The lock
 * triggers automatically on the very first expense / top-up — this modal
 * is just the gate.
 *
 * Dual-confirmation UX (user-mandated):
 *   1. Tick a checkbox: "I understand this rate is permanent"
 *   2. Type "LOCK" verbatim into a text input
 * Both must pass before the submit button enables.
 *
 * Backend contract:
 *   POST /api/groups/{id}/trip-rate
 *     body: { trip_currency, trip_rate, default_entry_currency, confirm_lock: true }
 *   Backend re-checks all of these and refuses if trip_rate_locked=true.
 */
import { useEffect, useState } from 'react';
import { AlertTriangle, Lock, Calculator, RefreshCw, Wifi, WifiOff } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { toast } from 'sonner';
import api from '../lib/api';
import { CURRENCY_SYMBOLS } from '../lib/formatNXC';

const TRIP_CURRENCY_OPTIONS = [
  { code: 'USD', label: 'US Dollar', symbol: '$' },
  { code: 'EUR', label: 'Euro', symbol: '€' },
  { code: 'GBP', label: 'British Pound', symbol: '£' },
  { code: 'AED', label: 'UAE Dirham', symbol: 'د.إ' },
  { code: 'SGD', label: 'Singapore Dollar', symbol: 'S$' },
  { code: 'AUD', label: 'Australian Dollar', symbol: 'A$' },
  { code: 'CAD', label: 'Canadian Dollar', symbol: 'C$' },
  { code: 'JPY', label: 'Japanese Yen', symbol: '¥' },
];

export default function SetTripRateModal({ open, onOpenChange, group, onSaved }) {
  const homeCurrency = group?.currency || 'INR';
  const homeSymbol = CURRENCY_SYMBOLS[homeCurrency] || homeCurrency;

  const [tripCurrency, setTripCurrency] = useState('EUR');
  const [tripRate, setTripRate] = useState('');
  const [defaultEntry, setDefaultEntry] = useState('trip');
  const [agreeForever, setAgreeForever] = useState(false);
  const [lockWord, setLockWord] = useState('');
  const [saving, setSaving] = useState(false);
  // iter192 — live FX pre-fill state. `live` holds the most-recent Frankfurter
  // result for the current pair. `liveStatus` is one of:
  //   'idle' (no fetch yet) | 'loading' | 'ok' | 'unavailable'
  const [live, setLive] = useState(null);
  const [liveStatus, setLiveStatus] = useState('idle');
  const [userTypedRate, setUserTypedRate] = useState(false);  // sticky once user edits

  // Reset state every time the modal re-opens so a stale "LOCK" doesn't carry over.
  useEffect(() => {
    if (open) {
      setTripCurrency('EUR');
      setTripRate('');
      setDefaultEntry('trip');
      setAgreeForever(false);
      setLockWord('');
      setSaving(false);
      setLive(null);
      setLiveStatus('idle');
      setUserTypedRate(false);
    }
  }, [open]);

  // iter192 — Whenever the modal is open and a new trip currency is picked,
  // fetch the live FX rate from the backend proxy and pre-fill the input
  // (unless the admin has already typed something — `userTypedRate` blocks
  // overwriting their input).
  useEffect(() => {
    if (!open) return;
    if (!tripCurrency || tripCurrency === homeCurrency) return;
    let aborted = false;
    setLiveStatus('loading');
    api.get(`/fx-rate?from=${tripCurrency}&to=${homeCurrency}`)
      .then((res) => {
        if (aborted) return;
        const r = Number(res.data?.rate);
        if (r > 0) {
          setLive({ rate: r, fetched_at: res.data?.fetched_at, ecb_date: res.data?.ecb_date, cached: !!res.data?.cached });
          setLiveStatus('ok');
          if (!userTypedRate) setTripRate(String(r));
        } else {
          setLive(null);
          setLiveStatus('unavailable');
          if (!userTypedRate) setTripRate('');
        }
      })
      .catch(() => {
        if (aborted) return;
        setLive(null);
        setLiveStatus('unavailable');
        // Clear stale pre-fill from a previous currency so admin doesn't lock
        // the EUR rate against an AED trip by mistake.
        if (!userTypedRate) setTripRate('');
      });
    return () => { aborted = true; };
  }, [open, tripCurrency, homeCurrency]);

  const handleRefreshLive = async () => {
    if (!tripCurrency || tripCurrency === homeCurrency) return;
    setLiveStatus('loading');
    try {
      const res = await api.get(`/fx-rate?from=${tripCurrency}&to=${homeCurrency}`);
      const r = Number(res.data?.rate);
      if (r > 0) {
        setLive({ rate: r, fetched_at: res.data?.fetched_at, ecb_date: res.data?.ecb_date, cached: !!res.data?.cached });
        setLiveStatus('ok');
        setTripRate(String(r));
        setUserTypedRate(false);
      } else {
        setLiveStatus('unavailable');
      }
    } catch {
      setLiveStatus('unavailable');
    }
  };

  const tripOption = TRIP_CURRENCY_OPTIONS.find((c) => c.code === tripCurrency);
  const tripSymbol = tripOption?.symbol || tripCurrency;
  const parsedRate = Number(tripRate);
  const rateValid = parsedRate > 0 && Number.isFinite(parsedRate);
  const sameAsHome = tripCurrency === homeCurrency;
  // Case-sensitive — typing 'lock' lowercase must NOT enable the button.
  const confirmationsOk = agreeForever && lockWord.trim() === 'LOCK';
  const submitDisabled = saving || !rateValid || sameAsHome || !confirmationsOk;

  const sampleEur = 100;
  const sampleHome = rateValid ? Math.round(sampleEur * parsedRate).toLocaleString('en-IN') : '—';

  const handleSubmit = async () => {
    if (submitDisabled) return;
    setSaving(true);
    try {
      const res = await api.post(`/groups/${group.id}/trip-rate`, {
        trip_currency: tripCurrency,
        trip_rate: parsedRate,
        default_entry_currency: defaultEntry,
        confirm_lock: true,
      });
      toast.success(res.data?.message || 'Trip rate locked in');
      onSaved?.(res.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save trip rate');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="set-trip-rate-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <Lock className="w-5 h-5 text-amber-600" />
            Set frozen trip rate
          </DialogTitle>
        </DialogHeader>

        {/* Permanence warning — top of modal so it's unmissable */}
        <div className="rounded-2xl border-2 border-amber-300 bg-amber-50 dark:bg-amber-950/30 px-4 py-3 mt-2" data-testid="permanence-warning">
          <div className="flex items-start gap-2.5">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-[12px] leading-relaxed text-amber-900 dark:text-amber-100">
              <p className="font-bold">This cannot be changed later.</p>
              <p className="mt-0.5 text-amber-800/90 dark:text-amber-200/90">
                Every expense and top-up will be converted with this rate. To change it, you&apos;d
                have to end this trip and start a new one.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4 mt-3">
          {/* Trip currency picker */}
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wide">Trip currency</Label>
            <Select value={tripCurrency} onValueChange={setTripCurrency}>
              <SelectTrigger className="h-11 border-2 rounded-xl" data-testid="trip-currency-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TRIP_CURRENCY_OPTIONS.map((c) => (
                  <SelectItem key={c.code} value={c.code} disabled={c.code === homeCurrency}>
                    <span className="font-bold">{c.symbol}</span>&nbsp;{c.code} · {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Home currency for settlement is <span className="font-bold">{homeSymbol} {homeCurrency}</span>.
            </p>
          </div>

          {/* Rate input */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-bold uppercase tracking-wide">
                Locked rate · 1 {tripSymbol} = ? {homeSymbol}
              </Label>
              {/* iter192 — live FX indicator */}
              {liveStatus === 'loading' && (
                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" data-testid="fx-status-loading">
                  <RefreshCw className="w-3 h-3 animate-spin" /> Fetching live rate…
                </span>
              )}
              {liveStatus === 'ok' && live && (
                <button
                  type="button"
                  onClick={handleRefreshLive}
                  className="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-300 hover:underline"
                  data-testid="fx-status-ok"
                  title={`ECB rate as of ${live.ecb_date}`}
                >
                  <Wifi className="w-3 h-3" /> Live · ECB {live.ecb_date}
                  <RefreshCw className="w-2.5 h-2.5 opacity-60" />
                </button>
              )}
              {liveStatus === 'unavailable' && (
                <span className="inline-flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300" data-testid="fx-status-unavailable">
                  <WifiOff className="w-3 h-3" /> Live rate unavailable — enter manually
                </span>
              )}
            </div>
            <Input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={tripRate}
              onChange={(e) => { setTripRate(e.target.value); setUserTypedRate(true); }}
              placeholder="e.g. 92.00"
              className="h-12 text-lg font-bold border-2 rounded-xl tabular-nums"
              data-testid="trip-rate-input"
            />
            {liveStatus === 'ok' && live && Number(tripRate) > 0 && Math.abs(Number(tripRate) - live.rate) > 0.0001 && (
              <p className="text-xs text-muted-foreground" data-testid="rate-edited-hint">
                Live rate is <span className="font-bold tabular-nums">{live.rate.toLocaleString('en-IN', { maximumFractionDigits: 4 })}</span>. You&apos;re locking a custom rate.
              </p>
            )}
            {rateValid && (
              <div className="flex items-center gap-2 text-[12px] text-primary bg-primary/5 border border-primary/20 rounded-lg px-2.5 py-1.5" data-testid="trip-rate-preview">
                <Calculator className="w-3.5 h-3.5" />
                Preview: <span className="font-bold tabular-nums">{tripSymbol}100 = {homeSymbol}{sampleHome}</span>
              </div>
            )}
            {sameAsHome && (
              <p className="text-xs text-rose-600">Trip currency must differ from {homeCurrency}.</p>
            )}
          </div>

          {/* Default entry currency */}
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wide">When adding an expense, default to:</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setDefaultEntry('trip')}
                className={`rounded-xl border-2 px-3 py-2.5 text-sm font-bold transition ${defaultEntry === 'trip' ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-card hover:border-primary/50'}`}
                data-testid="default-entry-trip"
              >
                {tripSymbol} {tripCurrency}
                <span className="block text-xs font-normal text-muted-foreground">(on the trip)</span>
              </button>
              <button
                type="button"
                onClick={() => setDefaultEntry('home')}
                className={`rounded-xl border-2 px-3 py-2.5 text-sm font-bold transition ${defaultEntry === 'home' ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-card hover:border-primary/50'}`}
                data-testid="default-entry-home"
              >
                {homeSymbol} {homeCurrency}
                <span className="block text-xs font-normal text-muted-foreground">(pre-paid in {homeCurrency})</span>
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              Each member can switch per expense — this just sets the default that opens.
            </p>
          </div>

          {/* Dual confirmation block */}
          <div className="rounded-2xl border-2 border-border bg-muted/30 p-3 space-y-3" data-testid="confirmation-block">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={agreeForever}
                onChange={(e) => setAgreeForever(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-primary"
                data-testid="agree-permanent-checkbox"
              />
              <span className="text-[12px] leading-relaxed">
                I understand this rate is <span className="font-bold">permanent for this trip</span> and applies to every expense and top-up from this point on.
              </span>
            </label>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide">
                Type <span className="font-mono bg-amber-100 dark:bg-amber-900/40 px-1.5 py-0.5 rounded">LOCK</span> to confirm
              </Label>
              <Input
                value={lockWord}
                onChange={(e) => setLockWord(e.target.value)}
                placeholder="LOCK"
                className="h-10 font-mono tracking-widest border-2 rounded-xl"
                autoCapitalize="characters"
                autoComplete="off"
                data-testid="lock-word-input"
              />
            </div>
          </div>
        </div>

        <div className="flex gap-2 mt-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1 h-11 rounded-xl"
            data-testid="cancel-trip-rate-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitDisabled}
            className="flex-1 h-11 rounded-xl bg-primary hover:bg-primary/90 font-bold"
            data-testid="confirm-trip-rate-btn"
          >
            <Lock className="w-4 h-4 mr-1.5" />
            {saving ? 'Locking…' : 'Lock rate'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
